import React from 'react';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import { Preload } from '@qover/atoms';
import SetStartDateNextDay from '@qover/utils/src/SetStartDateNextDay';
import UnsetStartDate from '@qover/utils/src/UnsetStartDate';
import { ConfirmationPage, PaymentPage } from '@qover/pages';
import { Wizard } from '@qover/hooks/src/useDraft';
import { Contract as DraftProtos } from '@qover-private/protos-web/qover/dojo/tesla/v1/contract';
import { Draft } from './domain/Draft';
import type { Variant } from './domain/Terms';
import { PriceInfo } from './domain/PriceInfo';
import { Path } from './domain/Step';
import { QuoteRedirection } from './components/TeslaRedirection';
import { PaymentRequest } from './domain/PaymentRequest';
import { productService } from './services/productService';
import { pushPurchase } from './domain/customDataLayer';
import { ConfirmationTitle } from './tesla.styles';
import * as DraftFactory from './domain/factories/DraftFactory';
import { TeslaPolicyholder } from './pages/policyholder/teslaPolicyholder.loadable';
import { TeslaQuote } from './pages/quote/teslaQuote.loadable';
import { TeslaSerialNumber } from './pages/teslaSerialNumber/teslaSerialNumber.loadable';
import { TeslaFinalization } from './pages/finalization/teslaFinalization.loadable';
import { TeslaSummary } from './pages/summary/teslaSummary.loadable';
import TeslaCreateDraft from './pages/createDraft/TeslaCreateDraft';

type RoutesProps = {
  currentStep?: Path;
  draft: Draft;
  handleSubmit: any;
  linkTo: any;
  match: any;
  prices: PriceInfo;
  terms: any;
  wizard: Wizard<Draft>;
};

export const Routes: React.FC<RoutesProps> = props => {
  const { currentStep, draft, linkTo, match } = props;

  const location = useLocation();

  const defaultProps = {
    buttonTitle: currentStep?.buttonTitle,
    draft,
    onEmailModalSubmit: (form: { email: string }) =>
      props.wizard.updateEmail('save', form.email),
    onSubmit: props.handleSubmit,
    prices: props.prices,
  };

  return (
    <Switch>
      <Route
        path={`${match.path}/quote`}
        render={p => (
          <>
            <Preload component={TeslaPolicyholder} />
            <TeslaCreateDraft
              handleCreateOnMount={props.wizard.handleCreateOnMount}
              match={match}
            />
            <TeslaQuote
              {...p}
              draft={props.draft}
              onSubmit={props.handleSubmit}
              prices={props.prices}
              step={props.currentStep?.path as string}
              wizard={props.wizard}
            />
          </>
        )}
      />
      <Route
        path={`${match.path}/policyholder`}
        render={() => (
          <QuoteRedirection match={match}>
            <UnsetStartDate />
            <Preload component={TeslaSerialNumber} />
            <TeslaPolicyholder {...defaultProps} backTo={linkTo('quote')} />
          </QuoteRedirection>
        )}
      />
      <Route
        path={`${match.path}/serial-number`}
        render={() => (
          <QuoteRedirection match={match}>
            <UnsetStartDate />
            <Preload component={TeslaFinalization} />
            <TeslaSerialNumber
              {...defaultProps}
              backTo={linkTo('policyholder')}
            />
          </QuoteRedirection>
        )}
      />
      <Route
        exact
        path={`${match.path}/finalization`}
        render={() => (
          <QuoteRedirection match={match}>
            <Preload component={TeslaSummary} />
            <SetStartDateNextDay />
            <TeslaFinalization
              {...defaultProps}
              backTo={linkTo('serial-number')}
            />
          </QuoteRedirection>
        )}
      />
      <Route
        exact
        path={`${match.path}/summary`}
        render={() => (
          <QuoteRedirection match={match}>
            <Preload component={PaymentPage} />
            <SetStartDateNextDay />
            <TeslaSummary
              draft={draft}
              linkTo={linkTo}
              onSubmit={props.handleSubmit}
              prices={props.prices}
              terms={props.terms}
              wizard={props.wizard}
            />
          </QuoteRedirection>
        )}
      />
      <Route
        path={`${match.path}/payment/:type(card|bancontact|direct-debit|sofort|ideal|giropay)?`}
        render={() => (
          <QuoteRedirection match={match}>
            <PaymentPage
              backTo={linkTo('summary')}
              draft={props.draft}
              match={match}
              paymentRequest={PaymentRequest}
              paymentMethodsContainerStyle={{
                backgroundColor: 'var(--color-card-background-100)',
              }}
              paymentItemContainerStyle={{
                boxShadow: 'none',
                borderRadius: '40px',
              }}
            />
          </QuoteRedirection>
        )}
      />
      <Route
        exact
        path={`${match.path}/confirmation`}
        render={() => (
          <ConfirmationPage
            backgroundClassName="top-background-position background-position-right background-no-repeat background-contain"
            productName="tesla"
            productService={productService}
            pushPurchase={response =>
              pushPurchase(
                DraftFactory.createFromApiResponse(response as DraftProtos),
                response,
              )
            }
            renderTitle={ConfirmationTitle}
          />
        )}
      />
      <Redirect
        from={match.path}
        push={false}
        to={`${match.path}/quote${location.search}`}
      />
    </Switch>
  );
};
